import React, { useMemo, useState } from "react";
import { uploadCustomCakeImage } from "../../utils/uploadCustomCakeImage";
import {
  Overlay,
  ModalCard,
  ModalHeader,
  ModalTitle,
  CloseBtn,
  ModalBody,
  CakePreview,
  CakeImg,
  CakeInfo,
  CakeName,
  CakeMeta,
  FieldsGrid,
  FieldGroup,
  FieldGroupWide,
  FieldLabel,
  SelectField,
  TextArea,
  HiddenFileInput,
  FileUploadRow,
  FileTrigger,
  FileName,
  FileHint,
  PriceBox,
  Footer,
  CancelBtn,
  ConfirmBtn,
} from "./CustomCakeModalStyles";

function roundPrice(value) {
  return Math.round(Number(value || 0) * 2) / 2;
}

function formatLei(value) {
  const rounded = roundPrice(value);

  return `${rounded.toLocaleString("ro-RO", {
    minimumFractionDigits: rounded % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  })} lei`;
}

function formatGramaj(g) {
  if (!g) return "";
  if (Number(g) >= 1000) return `${Number(g) / 1000} kg`;
  return `${g} g`;
}

function formatDisponibilitate(termenOre) {
  const ore = Number(termenOre || 0);

  if (ore <= 0) return "Disponibil imediat";

  const zile = Math.ceil(ore / 24);

  if (zile === 1) return "Disponibil în 1 zi";
  return `Disponibil în ${zile} zile`;
}

function buildGramajOptions(product) {
  const min = Number(product.gramaj_min || 0);
  const max = Number(product.gramaj_max || 0);
  const step = Number(product.pas_gramaj || 0);

  if (!min || !max || !step) return [];

  const arr = [];
  for (let value = min; value <= max; value += step) {
    arr.push(value);
  }
  return arr;
}

const optiuniPersonalizare = [
  { nume: "Medovik cu zmeură", pret_kg: 150 },
  { nume: "Napoleon cu caramel și mere", pret_kg: 150 },
  { nume: "Tort cu Banană și Căpșuni", pret_kg: 140 },
  { nume: "Tort Caramel și Arahide", pret_kg: 160 },
  {
    nume: "Tort cu blat de brownie, mousse de zmeură, ganache de ciocolată neagră cu alune și Nutella",
    pret_kg: 180,
  },
  { nume: "Tort cu blat de fistic și cremă de vanilie", pret_kg: 170 },
  {
    nume: "Tort cu cremă de vanilie și cremă de caramel cu crunch",
    pret_kg: 160,
  },
  { nume: "Tort cu cremă de vanilie și inserție de ciocolată", pret_kg: 150 },
  {
    nume: "Tort cu cremă de vanilie și inserție de zmeură și mousse de ciocolată",
    pret_kg: 160,
  },
  {
    nume: "Tort cu cremă de vanilie și mini eclere umplute cu fructe și cremă de fistic",
    pret_kg: 160,
  },
  { nume: "Tort cu gust de octombrie", pret_kg: 150 },
  { nume: "Tort cu mousse de ciocolată și inserție de zmeură", pret_kg: 150 },
  { nume: "Tort cu nutella alune", pret_kg: 150 },
  { nume: "Tort cu oreo", pret_kg: 150 },
  { nume: "Tort cu oreo și arahide", pret_kg: 150 },
  { nume: "Tort cu oreo și caramel", pret_kg: 150 },
  { nume: "Tort exotic", pret_kg: 180 },
  { nume: "Tort lămâie caramel", pret_kg: 150 },
  { nume: "Tort levănțică vanilie", pret_kg: 115 },
  { nume: "Tort medovik portocală", pret_kg: 180 },
  { nume: "Tort mousse de ciocolată", pret_kg: 150 },
  { nume: "Tort plombir cu vișină", pret_kg: 165 },
  { nume: "Tort poppy peach", pret_kg: 160 },
  { nume: "Tort red velvet", pret_kg: 150 },
  { nume: "Tort red velvet cu zmeură", pret_kg: 160 },
  { nume: "Tort tropic", pret_kg: 180 },
];

const optiuniBentoCake = [
  "Bento Cake 3 cioco Callebaut",
  "Bento Cake Mousse afine, căpșuni și cremă de vanilie Bourbon",
  "Bento Cake Mousse fructe de pădure, căpșuni și cremă de vanilie Bourbon",
  "Bento Cake Mousse de fructe de pădure, mousse de zmeură și cremă de vanilie Bourbon",
];

const decoruriBentoCake = [
  "Tom & Jerry",
  "Inimioară cu mesaj ascuns „Te iubesc”",
];

export default function CustomCakeModal({ product, onClose, onConfirm }) {
  const gramaje = useMemo(() => buildGramajOptions(product), [product]);

  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [gramaj, setGramaj] = useState(
    product.gramaj_min ?? product.gramaj ?? null,
  );
  const [personalizareNume, setPersonalizareNume] = useState("");
  const [personalizarePretKg, setPersonalizarePretKg] = useState(0);
  const [descriereDecor, setDescriereDecor] = useState("");
  const [mentiuni, setMentiuni] = useState("");
  const [pozaModelUrl, setPozaModelUrl] = useState("");

  const isBentoCake =
    product.nume?.toLowerCase().includes("bento") ||
    product.categorie === "bento_cake";

  const pretFinal = useMemo(() => {
    if (isBentoCake) {
      return roundPrice(product.pret || 0);
    }

    const pretPeKg = Number(personalizarePretKg || product.pret || 0);
    return roundPrice((pretPeKg * Number(gramaj || 0)) / 1000);
  }, [isBentoCake, personalizarePretKg, product.pret, gramaj]);

  return (
    <Overlay onClick={uploadingImage ? undefined : onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {isBentoCake ? "Configurează Bento Cake" : "Configurează tortul"}
          </ModalTitle>
          <CloseBtn
            type="button"
            onClick={onClose}
            aria-label="Închide"
            disabled={uploadingImage}
          >
            ×
          </CloseBtn>
        </ModalHeader>

        <ModalBody>
          <CakePreview>
            <CakeImg src={product.imagine} alt={product.nume} />

            <CakeInfo>
              <CakeName>{product.nume}</CakeName>
              <CakeMeta>{formatDisponibilitate(product.termen_ore)}</CakeMeta>
            </CakeInfo>
          </CakePreview>

          <FieldsGrid>
            {isBentoCake ? (
              <>
                <FieldGroupWide>
                  <FieldLabel>Sortiment Bento Cake</FieldLabel>
                  <SelectField
                    value={personalizareNume}
                    onChange={(e) => setPersonalizareNume(e.target.value)}
                  >
                    <option value="">Alege sortimentul</option>
                    {optiuniBentoCake.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </SelectField>
                </FieldGroupWide>

                <FieldGroupWide>
                  <FieldLabel>Decor Bento Cake</FieldLabel>
                  <SelectField
                    value={descriereDecor}
                    onChange={(e) => setDescriereDecor(e.target.value)}
                  >
                    <option value="">Alege decorul</option>
                    {decoruriBentoCake.map((decor) => (
                      <option key={decor} value={decor}>
                        {decor}
                      </option>
                    ))}
                  </SelectField>
                </FieldGroupWide>
              </>
            ) : (
              <>
                <FieldGroupWide>
                  <FieldLabel>Sortiment / personalizare</FieldLabel>
                  <SelectField
                    value={personalizareNume}
                    onChange={(e) => {
                      const selected = optiuniPersonalizare.find(
                        (opt) => opt.nume === e.target.value,
                      );

                      setPersonalizareNume(selected?.nume || "");
                      setPersonalizarePretKg(Number(selected?.pret_kg || 0));
                    }}
                  >
                    <option value="">Alege personalizarea</option>
                    {optiuniPersonalizare.map((opt) => (
                      <option key={opt.nume} value={opt.nume}>
                        {opt.nume} ({formatLei(opt.pret_kg)} / kg)
                      </option>
                    ))}
                  </SelectField>
                </FieldGroupWide>

                <FieldGroup>
                  <FieldLabel>Greutate</FieldLabel>
                  <SelectField
                    value={gramaj || ""}
                    onChange={(e) => setGramaj(Number(e.target.value))}
                  >
                    {gramaje.map((g) => (
                      <option key={g} value={g}>
                        {formatGramaj(g)}
                      </option>
                    ))}
                  </SelectField>
                </FieldGroup>

                <FieldGroupWide>
                  <FieldLabel>Decor dorit</FieldLabel>
                  <TextArea
                    value={descriereDecor}
                    onChange={(e) => setDescriereDecor(e.target.value)}
                    placeholder="Descrie cum vrei să arate tortul"
                  />
                </FieldGroupWide>
              </>
            )}

            {!isBentoCake && (
              <>
                <FieldGroupWide>
                  <FieldLabel>Mențiuni</FieldLabel>
                  <TextArea
                    value={mentiuni}
                    onChange={(e) => setMentiuni(e.target.value)}
                    placeholder="Ex: mesaj pe tort, culori dorite, alte detalii importante"
                  />
                </FieldGroupWide>

                <FieldGroupWide>
                  <FieldLabel>Poză model</FieldLabel>

                  <HiddenFileInput
                    id="custom-cake-poza-model"
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      try {
                        setUploadError("");
                        setUploadingImage(true);

                        const publicUrl = await uploadCustomCakeImage(file);
                        setPozaModelUrl(publicUrl);
                      } catch (err) {
                        setUploadError(
                          err?.message ||
                            "Nu am putut încărca poza model. Încearcă din nou.",
                        );
                        setPozaModelUrl("");
                      } finally {
                        setUploadingImage(false);
                        e.target.value = "";
                      }
                    }}
                  />

                  <FileUploadRow>
                    <FileTrigger htmlFor="custom-cake-poza-model">
                      {uploadingImage ? "Se încarcă..." : "Adaugă poză"}
                    </FileTrigger>

                    <FileName>
                      {uploadingImage
                        ? "Se încarcă..."
                        : pozaModelUrl
                          ? "Poză încărcată"
                          : "Nicio poză selectată"}
                    </FileName>
                  </FileUploadRow>

                  {pozaModelUrl ? (
                    <FileHint
                      href={pozaModelUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Vezi poza adăugată
                    </FileHint>
                  ) : (
                    <FileHint as="span">
                      Poți încărca o imagine model pentru tort.
                    </FileHint>
                  )}

                  {uploadError ? (
                    <FileHint as="span">{uploadError}</FileHint>
                  ) : null}
                </FieldGroupWide>
              </>
            )}

            <FieldGroupWide>
              <PriceBox>
                <FieldLabel>Preț estimat</FieldLabel>
                <strong>{formatLei(pretFinal)}</strong>
              </PriceBox>
            </FieldGroupWide>
          </FieldsGrid>
        </ModalBody>

        <Footer>
          <CancelBtn type="button" onClick={onClose} disabled={uploadingImage}>
            Renunță
          </CancelBtn>

          <ConfirmBtn
            type="button"
            disabled={
              !personalizareNume ||
              uploadingImage ||
              (isBentoCake ? !descriereDecor : !gramaj)
            }
            onClick={() =>
              onConfirm({
                cantitate: 1,
                gramaj: isBentoCake ? product.gramaj : gramaj,
                mentiuni: isBentoCake ? "" : mentiuni,
                poza_model_url: isBentoCake ? "" : pozaModelUrl,
                personalizare_nume: personalizareNume,
                personalizare_pret_kg: isBentoCake ? 0 : personalizarePretKg,
                descriere_decor: descriereDecor,
              })
            }
          >
            {uploadingImage ? "Se încarcă poza..." : "Adaugă în coș"}
          </ConfirmBtn>
        </Footer>
      </ModalCard>
    </Overlay>
  );
}
